import React from 'react';
import styled from 'styled-components';

import {
  CoordinateSystemModel,
  FetchSharedViewResponse,
  FindProjectModel,
  FindProjectResponse,
  GeoidModel,
} from '../../../../../typings/api/skymap/rest/v0/.common';
import { TokenService } from '../../../services/authentication/token.service';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { GeodataStoreFactory } from '../../../stores/geodata-store-factory/geodata-store-factory';
import { resolveApiUrl } from '../../../utils/core/endpoints';
import { isDefined } from '../../../utils/variables';
import { AssetManager } from '../../../viewer/asset-manager';
import cameraManager from '../../../viewer/camera-manager';
import { InputHandler } from '../../../viewer/input-handler';
import { publish, SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { ProjectGeodataManager } from '../../../viewer/project-geodata-manager';
import { SkyView } from '../../../viewer/skyview';
import state from '../../../viewer/state';
import { Tool } from '../../../viewer/tools/tool';
import { Point2d } from '../../../viewer/utilities/math-helpers';
import { viewerZIndices } from '../../../viewer/viewer-z-indices';
import { getHttpStatusCode } from '../../api/error-handling';
import { DesignLicenseDialog } from '../../components/design-license-dialog/design-license-dialog';
import { SkyViewContextMenu, SkyViewContextMenuProps } from '../../components/skyview/context-menu';
import { SkyViewLoader } from '../../components/skyview/skyview-loader';
import {
  SkyviewAssetPositionProps,
  SkyviewAssetPositionTooltip,
} from '../../components/skyview/tooltip/skyview-asset-position-tooltip';
import { VolumeReportCanvas } from '../../components/skyview-new/components/report-tool-menu/volume-overview-pdf-report/volume-report-canvas';
import { SaveGeometryAssetDialog } from '../../components/skyview-new/components/save-geometry-asset-dialog';
import { SetAssetCoordinate } from '../../components/skyview-new/components/set-asset-coordinate/set-asset-coordinate';
import { Skyview } from '../../components/skyview-new/skyview';
import { SkyviewCanvasContainer } from '../../components/skyview-new/skyview-canvas-container';
import { useAssetsStore } from '../../components/skyview-new/state/assets-store';
import { useSubscribe } from '../../hooks/use-subscribe';
import { ProjectReferenceProvider } from '../../state/project-reference-state';
import { clearSharedViewProjectId, setSharedViewProjectId } from './shared-view-project-adapter';

type Props = {
  sharedViewId: string;
};

type LoadState = 'idle' | 'loading' | 'ready' | 'error' | 'notFound';

type SpatialReferenceSeed = {
  coordinateSystem?: CoordinateSystemModel;
  geoid?: GeoidModel;
};

const tooltipTools: Set<string> = new Set([
  'measure',
  'select',
  'design',
  'asbuilt-geometry',
  'reference-point-transform',
  'volume-inspector',
]);

export const contextMenuTools: Set<string> = new Set([
  'measure',
  'select',
  'design',
  'reference-point-transform',
  'transform',
  'volume-inspector',
  'intersection',
]);

type ToolWithUiHooks = Tool & {
  onTooltipChanged?: (callback: (tooltip?: SkyviewAssetPositionProps) => void) => void;
  onContextMenuChanged?: (callback: (menu?: SkyViewContextMenuProps) => void) => void;
};

// eslint-disable-next-line unused-imports/no-unused-vars
type SkyviewWindow = Window & { skyview?: SkyView };

const SharedViewPage = ({ sharedViewId }: Props) => {
  const [skyView, setSkyView] = React.useState<SkyView>();
  const [loadState, setLoadState] = React.useState<LoadState>('loading');
  const [, setUiTick] = React.useState(0);
  const [assetTooltip, setAssetTooltip] = React.useState<SkyviewAssetPositionProps>();
  const [contextMenu, setContextMenu] = React.useState<SkyViewContextMenuProps>();
  const [assetMenuVisible, setAssetMenuVisible] = React.useState(false);
  const [accessError, setAccessError] = React.useState<string | undefined>();
  const [spatialReferenceSeed, setSpatialReferenceSeed] = React.useState<SpatialReferenceSeed>();
  const [sharedViewData, setSharedViewData] = React.useState<FetchSharedViewResponse['data']>();

  // Keep a mutable reference to ensure cleanup even if state changes during async work.
  const viewerRef = React.useRef<{ skyview?: SkyView }>({});
  const skyViewRef = React.useRef<SkyView>();
  const viewerContainerRef = React.useRef<HTMLDivElement>(null);
  const loadStartedRef = React.useRef(false);
  const periodicCheckIntervalRef = React.useRef<number | undefined>();
  const viewer = viewerRef.current;

  const webglSupported = React.useMemo(() => !!window.WebGLRenderingContext, []);

  const registerToolCallbacks = React.useCallback(
    (tool?: ToolWithUiHooks) => {
      setAssetTooltip(undefined);
      setContextMenu(undefined);

      if (!tool) {
        return;
      }

      if (tooltipTools.has(tool.name)) {
        tool.onTooltipChanged?.((tooltip?: SkyviewAssetPositionProps) => setAssetTooltip(tooltip));
      } else {
        tool.onTooltipChanged?.(() => setAssetTooltip(undefined));
      }

      if (contextMenuTools.has(tool.name)) {
        tool.onContextMenuChanged?.((menu?: SkyViewContextMenuProps) => setContextMenu(menu));
      } else {
        tool.onContextMenuChanged?.(() => setContextMenu(undefined));
      }
    },
    [setAssetTooltip, setContextMenu],
  );

  useSubscribe(SubscriptionTopic.SetTool, (tool) => {
    registerToolCallbacks(tool as ToolWithUiHooks);
  });

  React.useEffect(() => {
    if (!skyView) {
      return;
    }

    registerToolCallbacks(InputHandler.instance.currentTool() as ToolWithUiHooks);
  }, [registerToolCallbacks, skyView]);

  // Periodic access check (every hour)
  const checkAccess = React.useCallback(async () => {
    try {
      const sharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();
      await sharedViewService.getProjectInfo({ path: { id: sharedViewId } });
    } catch (err: unknown) {
      const statusCode = getHttpStatusCode(err);
      if (statusCode !== undefined && statusCode !== 200) {
        setAccessError(
          'Länken är inte längre giltig. Kontakta projektets administratör för att begära en ny länk.',
        );
        setLoadState('error');
      }
    }
  }, [sharedViewId]);

  React.useEffect(() => {
    if (!webglSupported) {
      setLoadState('error');
      return;
    }

    if (loadStartedRef.current) {
      return () => undefined;
    }
    loadStartedRef.current = true;

    const cancelledRef = { current: false };

    const loadSharedView = async () => {
      setLoadState('loading');
      setAccessError(undefined);

      try {
        // Setup axios service factory
        const tokenService = new TokenService();
        SkyMapAxiosServiceFactory.instance.setup(resolveApiUrl(), tokenService);

        const sharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();

        // Fetch project info
        const projectInfoResponse: FindProjectResponse = await sharedViewService.getProjectInfo({
          path: { id: sharedViewId },
        });
        const projectInfo: FindProjectModel | undefined = projectInfoResponse.data;

        if (!projectInfo) {
          throw new Error('Project info is missing');
        }

        // Fetch shared view data
        const sharedViewResponse: FetchSharedViewResponse = await sharedViewService.fetchById({
          path: { id: sharedViewId },
        });

        // Determine if asset menu should be visible
        const hasAssets =
          sharedViewResponse.data.dynamicAssets.length +
            sharedViewResponse.data.pois.length +
            sharedViewResponse.data.staticAssets.length >
          0;
        setAssetMenuVisible(hasAssets);
        setSharedViewData(sharedViewResponse.data);

        // Set camera transformation
        cameraManager().setCameraTransformation(sharedViewResponse.data.cameraTransformation);

        // Set shared view ID for geodata store factory
        GeodataStoreFactory.instance.setSharedViewId(sharedViewId);

        // Initialize asset manager with shared view flag
        AssetManager.instance.initialise(viewer, true, sharedViewId);

        // Pass saved CAD visibility so hidden assets are not force-loaded.
        AssetManager.instance.setSharedViewAssetVisibilities(sharedViewResponse.data.dynamicAssets);

        // Set shared view flag for geodata manager
        ProjectGeodataManager.instance().isSharedView = true;

        // Load geodata with shared view ID
        ProjectGeodataManager.instance().load(projectInfo, sharedViewId);

        // Create and initialize SkyView
        const skyviewInstance = new SkyView(projectInfo, {
          sceneManagerOptions: {
            defaultTimelineContainerId: projectInfo.defaultTimelineContainerId,
            defaultSceneType: sharedViewResponse.data.sceneType,
          },
          viewerElement: viewerContainerRef.current ?? undefined,
        });

        viewer.skyview = skyviewInstance;
        skyViewRef.current = skyviewInstance;

        await skyviewInstance.init();

        if (cancelledRef.current) {
          skyviewInstance.clearCustom();
          return;
        }

        // Set occlusion culling from shared view response
        // @ts-ignore - skyview property exists but may not be typed
        viewer.skyview.setOcclusionCulling(sharedViewResponse.data.occlusionCulling);

        // TODO: This global assignment may be needed for backward compatibility with external scripts,
        // but should be refactored to remove this dependency in the future.
        // Commented out to improve maintainability and readability, but kept for reference.
        // If external scripts depend on window.skyview, this may need to be uncommented temporarily.
        // (window as unknown as SkyviewWindow).skyview = skyviewInstance;

        setSharedViewProjectId(projectInfo.id);
        setSpatialReferenceSeed({
          coordinateSystem: projectInfo.coordinateSystemModel,
          geoid: projectInfo.geoidModel,
        });
        setSkyView(skyviewInstance);
        setLoadState('ready');
        setUiTick((value) => value + 1);

        // Start periodic access check (every hour)
        periodicCheckIntervalRef.current = window.setInterval(checkAccess, 60 * 60 * 1000);
      } catch (err: unknown) {
        if (!cancelledRef.current) {
          // eslint-disable-next-line no-console
          console.error('Failed to load shared view', err);
          const statusCode = getHttpStatusCode(err);
          if (statusCode === 404) {
            setLoadState('notFound');
          } else {
            setLoadState('error');
          }
        }
      }
    };

    void loadSharedView();

    return () => {
      setAssetTooltip(undefined);
      setContextMenu(undefined);

      // Clear periodic check interval
      if (periodicCheckIntervalRef.current !== undefined) {
        clearInterval(periodicCheckIntervalRef.current);
        periodicCheckIntervalRef.current = undefined;
      }

      const skyviewToClear = skyViewRef.current;

      if (skyviewToClear) {
        cancelledRef.current = true;
        skyviewToClear.clearCustom();
        skyViewRef.current = undefined;
      }

      viewer.skyview = undefined;
      AssetManager.instance.clearCustom();
      clearSharedViewProjectId();
      loadStartedRef.current = false;
    };
  }, [sharedViewId, webglSupported, viewer, checkAccess]);

  React.useEffect(() => {
    if (!skyView) {
      return;
    }

    let frameHandle: number | undefined;

    const onUpdateUI = () => {
      if (frameHandle) {
        cancelAnimationFrame(frameHandle);
      }

      frameHandle = requestAnimationFrame(() => setUiTick((value) => value + 1));
    };

    skyView.addEventListener('updateUI', onUpdateUI);

    return () => {
      if (frameHandle) {
        cancelAnimationFrame(frameHandle);
      }
      skyView.removeEventListener('updateUI', onUpdateUI);
    };
  }, [skyView]);

  const isNativeApp = Boolean(state.getValue('isNativeApp'));
  const showLoader = loadState === 'loading' || (skyView && !skyView.getActiveScene());
  const showSpinner = Boolean(skyView && skyView.isLoading() && !isNativeApp);
  const showViewer = skyView && skyView.getActiveScene() && !isNativeApp && loadState === 'ready';

  const gpsIconVisible = Boolean(state.getValue('gpsIconVisible'));
  const gpsMarker: Point2d | undefined =
    skyView?.isOrthoViewActive() && gpsIconVisible ? skyView.updatedScreenRatio : undefined;

  const SaveGeometryAssetDialogComponent = SaveGeometryAssetDialog as React.ComponentType;
  const VolumeReportCanvasComponent = VolumeReportCanvas as React.ComponentType;
  const SetAssetCoordinateComponent = SetAssetCoordinate as React.ComponentType;
  const DesignLicenseDialogComponent = DesignLicenseDialog as React.ComponentType;

  const skyviewChildren = (
    <>
      <SharedViewAssetVisibility sharedViewData={sharedViewData} />
      <SetAssetCoordinateComponent />
      <SaveGeometryAssetDialogComponent />
      <DesignLicenseDialogComponent />
      <VolumeReportCanvasComponent />
    </>
  );

  if (!webglSupported) {
    return (
      <FullPageFallback id="webGlNotSupported">
        <span>SkyView kan inte visas då webbläsaren saknar stöd för WebGL.</span>
      </FullPageFallback>
    );
  }

  if (loadState === 'notFound') {
    return (
      <FullPageFallback>
        <ErrorMessageBox>
          <div>
            <h1>Länken är inte giltig</h1>
            <section>
              <NotFoundIcon className="fas fa-share-nodes" />
              <span>
                Länken du försöker nå är inte längre giltig.
                <br />
                Kontakta projektets administratör för att begära en ny länk till SkyView.
              </span>
            </section>
          </div>
        </ErrorMessageBox>
      </FullPageFallback>
    );
  }

  if (loadState === 'error') {
    return (
      <FullPageFallback>
        <ErrorMessageBox>
          <div>
            <h1>Länken kunde inte hämtas</h1>
            <section>
              <ErrorIcon className="fas fa-exclamation-circle" />
              <span>
                {accessError ||
                  'Det gick inte att öppna länken. Vänligen försök igen eller kontakta vår support om felet kvarstår.'}
              </span>
            </section>
          </div>
        </ErrorMessageBox>
      </FullPageFallback>
    );
  }

  return (
    <ViewerShell data-testid="viewer" id="viewer" ref={viewerContainerRef}>
      <SkyviewCanvasContainer />

      {showLoader && <SkyViewLoader />}

      {showViewer && gpsMarker && (
        <GpsMarker style={{ top: `${gpsMarker.y}%`, left: `${gpsMarker.x}%` }}>
          <div className="fas fa-map-marker gps-marker" />
        </GpsMarker>
      )}

      {showViewer && assetTooltip && (
        <SkyviewAssetPositionTooltip
          coordinate={assetTooltip.coordinate}
          coordinateSystem={assetTooltip.coordinateSystem}
          heights={assetTooltip.heights}
          properties={assetTooltip.properties}
          screenPoint={assetTooltip.screenPoint}
        />
      )}

      {showViewer && contextMenu && (
        <SkyViewContextMenu items={contextMenu.items} pos={contextMenu.pos} />
      )}

      {showViewer && skyView && (
        <ProjectReferenceProvider seed={spatialReferenceSeed}>
          <Skyview assetMenuVisible={assetMenuVisible} showSpinner={showSpinner} skyView={skyView}>
            {skyviewChildren}
          </Skyview>
        </ProjectReferenceProvider>
      )}
    </ViewerShell>
  );
};

export { SharedViewPage };

export type SharedViewAssetVisibilityProps = {
  sharedViewData?: FetchSharedViewResponse['data'];
};

export const SharedViewAssetVisibility = ({ sharedViewData }: SharedViewAssetVisibilityProps) => {
  const { findByAssetId } = useAssetsStore();

  React.useEffect(() => {
    if (!sharedViewData) {
      return;
    }

    const filterVisibleAssets = (savedAssets: { id: string; visible: boolean }[]) => {
      const visibleUuids: string[] = [];
      for (const savedAsset of savedAssets) {
        if (!savedAsset.visible) {
          continue;
        }
        const loaded = findByAssetId(savedAsset.id);
        if (isDefined(loaded) && loaded.visible) {
          visibleUuids.push(loaded.uuid);
        }
      }
      return visibleUuids;
    };

    publish(SubscriptionTopic.ShowOnlySpecificAssets, {
      assetUuids: [
        ...filterVisibleAssets(sharedViewData.dynamicAssets),
        ...filterVisibleAssets(sharedViewData.pois),
        ...filterVisibleAssets(sharedViewData.staticAssets),
      ],
    });
  }, [sharedViewData, findByAssetId]);

  return null;
};

const ViewerShell = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #0f0f10;
`;

const FullPageFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
  text-align: center;
  padding: 1rem;
  box-sizing: border-box;
`;

const ErrorMessageBox = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  margin-top: 100px;

  h1 {
    color: #666;
    margin: 0;
  }

  section {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 40px;

    i {
      font-size: 60px;
    }

    span {
      width: 400px;
      margin-left: 30px;
      line-height: 1.3em;
    }
  }
`;

const NotFoundIcon = styled.i`
  color: #888;
`;

const ErrorIcon = styled.i`
  color: #a00;
`;

const GpsMarker = styled.div`
  position: absolute;
  z-index: ${viewerZIndices.menu};
`;
